import React from 'react';
import {
  Box,
  Skeleton,
  Card,
  CardContent,
  CardMedia,
  Grid,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';

// Menu Item Skeleton
export const MenuItemSkeleton = ({ count = 6, viewMode = 'grid' }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const getGridSize = () => {
    if (isMobile) return 12;
    if (isTablet) return 6;
    return 4;
  };

  return (
    <Grid container spacing={isMobile ? 1 : 2}>
      {Array.from({ length: count }).map((_, index) => (
        <Grid item xs={12} sm={6} md={4} lg={getGridSize()} key={index}>
          <Card
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              borderRadius: 3,
              overflow: 'hidden',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              transition: 'all 0.3s ease-in-out',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
              },
            }}
          >
            <Skeleton
              variant="rectangular"
              height={isMobile ? 180 : 200}
              animation="wave"
              sx={{
                bgcolor: 'grey.200',
                '&::after': {
                  background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)',
                },
              }}
            />
            <CardContent sx={{ p: 2, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Skeleton variant="text" width="70%" height={24} animation="wave" />
                <Skeleton variant="circular" width={24} height={24} animation="wave" />
  </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Skeleton variant="text" width="60%" height={16} animation="wave" />
                <Skeleton variant="text" width="20%" height={16} animation="wave" />
      </Box>
              
              <Skeleton variant="text" width="100%" height={14} animation="wave" sx={{ mb: 1 }} />
              <Skeleton variant="text" width="80%" height={14} animation="wave" sx={{ mb: 2 }} />
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto' }}>
                <Skeleton variant="text" width="30%" height={20} animation="wave" />
                <Skeleton variant="rectangular" width={80} height={32} animation="wave" sx={{ borderRadius: 2 }} />
      </Box>
    </CardContent>
  </Card>
        </Grid>
      ))}
    </Grid>
  );
};

// Category Skeleton
export const CategorySkeleton = ({ count = 6 }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', pb: 1, px: 1 }}>
      {Array.from({ length: count }).map((_, index) => (
        <Box key={index} sx={{ minWidth: 120, flexShrink: 0 }}>
          <Skeleton
            variant="rectangular"
            height={isMobile ? 80 : 100}
            width={isMobile ? 100 : 120}
            animation="wave"
            sx={{
              borderRadius: 2,
              bgcolor: 'grey.200',
              '&::after': {
                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)',
              },
            }}
          />
          <Skeleton variant="text" width="100%" height={16} animation="wave" sx={{ mt: 1 }} />
        </Box>
      ))}
    </Box>
  );
};

// Hero Section Skeleton
export const HeroSkeleton = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box
      sx={{
        position: 'relative',
        height: isMobile ? 300 : 400,
        overflow: 'hidden',
        borderRadius: 3,
        mb: 4,
      }}
    >
      <Skeleton
        variant="rectangular"
        height="100%"
        width="100%"
        animation="wave"
        sx={{
          bgcolor: 'grey.200',
          '&::after': {
            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)',
          },
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          textAlign: 'center',
          color: 'white',
          zIndex: 2,
        }}
      >
        <Skeleton variant="text" width={300} height={40} animation="wave" sx={{ mb: 2, bgcolor: 'rgba(255,255,255,0.3)' }} />
        <Skeleton variant="text" width={200} height={24} animation="wave" sx={{ bgcolor: 'rgba(255,255,255,0.3)' }} />
      </Box>
      </Box>
  );
};

// Outlet Card Skeleton
export const OutletCardSkeleton = ({ count = 3 }) => {
  return (
    <Grid container spacing={2}>
      {Array.from({ length: count }).map((_, index) => (
        <Grid item xs={12} sm={6} md={4} key={index}>
          <Card sx={{ p: 3, borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Skeleton variant="circular" width={40} height={40} animation="wave" />
              <Box sx={{ flexGrow: 1 }}>
                <Skeleton variant="text" width="70%" height={20} animation="wave" />
                <Skeleton variant="text" width="50%" height={16} animation="wave" />
        </Box>
      </Box>
            <Skeleton variant="text" width="100%" height={14} animation="wave" sx={{ mb: 1 }} />
            <Skeleton variant="text" width="80%" height={14} animation="wave" sx={{ mb: 2 }} />
            <Skeleton variant="rectangular" width="100%" height={36} animation="wave" sx={{ borderRadius: 2 }} />
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};

// Search Bar Skeleton
export const SearchBarSkeleton = () => {
  return (
    <Box sx={{ mb: 3 }}>
      <Skeleton
        variant="rectangular"
        height={56}
        animation="wave"
        sx={{
          borderRadius: 3,
          bgcolor: 'grey.200',
          '&::after': {
            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)',
          },
        }}
      />
    </Box>
  );
};

// Filter Section Skeleton
export const FilterSectionSkeleton = () => {
  return (
    <Box sx={{ mb: 3, p: 3, bgcolor: 'grey.50', borderRadius: 3 }}>
      <Skeleton variant="text" width="30%" height={24} animation="wave" sx={{ mb: 2 }} />
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        {Array.from({ length: 4 }).map((_, index) => (
          <Skeleton
            key={index}
            variant="rectangular"
            width={80}
            height={32}
            animation="wave"
            sx={{ borderRadius: 2 }}
          />
        ))}
      </Box>
    </Box>
  );
};

// Page Header Skeleton
export const PageHeaderSkeleton = () => {
  return (
    <Box sx={{ mb: 4, textAlign: 'center' }}>
      <Skeleton variant="text" width="60%" height={40} animation="wave" sx={{ mx: 'auto', mb: 2 }} />
      <Skeleton variant="text" width="40%" height={24} animation="wave" sx={{ mx: 'auto' }} />
    </Box>
  );
};

// Cart Item Skeleton
export const CartItemSkeleton = ({ count = 3 }) => {
  return (
    <Box>
      {Array.from({ length: count }).map((_, index) => (
        <Box key={index} sx={{ display: 'flex', gap: 2, p: 2, mb: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
          <Skeleton variant="rectangular" width={60} height={60} animation="wave" sx={{ borderRadius: 1 }} />
          <Box sx={{ flexGrow: 1 }}>
            <Skeleton variant="text" width="70%" height={20} animation="wave" sx={{ mb: 1 }} />
            <Skeleton variant="text" width="50%" height={16} animation="wave" sx={{ mb: 1 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Skeleton variant="text" width="30%" height={16} animation="wave" />
              <Skeleton variant="rectangular" width={80} height={24} animation="wave" sx={{ borderRadius: 1 }} />
            </Box>
          </Box>
              </Box>
            ))}
              </Box>
  );
};

// Loading Overlay
export const LoadingOverlay = ({ message = "Loading..." }) => {
  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        bgcolor: 'rgba(255, 255, 255, 0.9)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        backdropFilter: 'blur(4px)',
      }}
    >
      <Box sx={{ textAlign: 'center' }}>
        <Skeleton
          variant="circular"
          width={60}
          height={60}
          animation="wave"
          sx={{
            mb: 2,
            bgcolor: 'primary.main',
            '&::after': {
              background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)',
            },
          }}
        />
        <Typography variant="h6" color="text.primary" sx={{ fontWeight: 600 }}>
          {message}
        </Typography>
            </Box>
    </Box>
  );
};

// Dashboard Skeleton
export const DashboardSkeleton = () => {
  return (
    <Box sx={{ p: 3 }}>
    <Box sx={{ mb: 4 }}>
        <Skeleton variant="text" width="40%" height={40} animation="wave" sx={{ mb: 2 }} />
        <Skeleton variant="text" width="60%" height={24} animation="wave" />
    </Box>

    <Grid container spacing={3} sx={{ mb: 4 }}>
      {Array.from({ length: 4 }).map((_, index) => (
        <Grid item xs={12} sm={6} md={3} key={index}>
            <Card sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Skeleton variant="circular" width={40} height={40} animation="wave" />
                <Box sx={{ flexGrow: 1 }}>
                  <Skeleton variant="text" width="70%" height={20} animation="wave" />
                  <Skeleton variant="text" width="50%" height={16} animation="wave" />
                </Box>
              </Box>
              <Skeleton variant="text" width="100%" height={14} animation="wave" />
          </Card>
        </Grid>
      ))}
    </Grid>

    <Grid container spacing={3}>
      <Grid item xs={12} md={8}>
          <Card sx={{ p: 3 }}>
            <Skeleton variant="text" width="30%" height={24} animation="wave" sx={{ mb: 2 }} />
            <Skeleton variant="rectangular" height={300} animation="wave" />
        </Card>
      </Grid>
      <Grid item xs={12} md={4}>
          <Card sx={{ p: 3 }}>
            <Skeleton variant="text" width="40%" height={24} animation="wave" sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {Array.from({ length: 5 }).map((_, index) => (
                <Box key={index} sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <Skeleton variant="circular" width={32} height={32} animation="wave" />
                  <Box sx={{ flexGrow: 1 }}>
                    <Skeleton variant="text" width="80%" height={16} animation="wave" />
                    <Skeleton variant="text" width="60%" height={14} animation="wave" />
                  </Box>
                </Box>
              ))}
            </Box>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

// Delivery Management Skeleton
export const DeliveryManagementSkeleton = () => {
  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Skeleton variant="text" width="30%" height={32} animation="wave" />
        <Skeleton variant="rectangular" width={120} height={36} animation="wave" sx={{ borderRadius: 2 }} />
      </Box>
      
      <Card sx={{ p: 3, mb: 3 }}>
        <Skeleton variant="text" width="25%" height={24} animation="wave" sx={{ mb: 2 }} />
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
          {Array.from({ length: 4 }).map((_, index) => (
            <Skeleton key={index} variant="rectangular" width={100} height={32} animation="wave" sx={{ borderRadius: 2 }} />
          ))}
        </Box>
        <Skeleton variant="rectangular" height={400} animation="wave" />
      </Card>
    </Box>
  );
};

// Page Skeleton (Generic)
export const PageSkeleton = () => {
  return (
    <Box sx={{ p: 3 }}>
      <Skeleton variant="text" width="40%" height={40} animation="wave" sx={{ mb: 3 }} />
      <Skeleton variant="rectangular" height={200} animation="wave" sx={{ mb: 3, borderRadius: 2 }} />
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        {Array.from({ length: 3 }).map((_, index) => (
          <Skeleton key={index} variant="rectangular" width={120} height={40} animation="wave" sx={{ borderRadius: 2 }} />
        ))}
      </Box>
      <Skeleton variant="rectangular" height={300} animation="wave" sx={{ borderRadius: 2 }} />
    </Box>
  );
};

// Skeleton Table
export const SkeletonTable = ({ rows = 5, columns = 4 }) => {
  return (
    <Box sx={{ overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', gap: 2, mb: 2, p: 2, bgcolor: 'grey.50' }}>
        {Array.from({ length: columns }).map((_, index) => (
          <Skeleton key={index} variant="text" width="100%" height={20} animation="wave" />
        ))}
      </Box>
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <Box key={rowIndex} sx={{ display: 'flex', gap: 2, p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          {Array.from({ length: columns }).map((_, colIndex) => (
            <Skeleton key={colIndex} variant="text" width="100%" height={16} animation="wave" />
          ))}
        </Box>
      ))}
    </Box>
  );
};

// Checkout Page Skeleton
export const CheckoutPageSkeleton = () => {
  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Skeleton variant="text" width="30%" height={32} animation="wave" sx={{ mb: 3 }} />
      
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card sx={{ p: 3, mb: 3 }}>
            <Skeleton variant="text" width="25%" height={24} animation="wave" sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <Skeleton variant="rectangular" width={80} height={32} animation="wave" sx={{ borderRadius: 2 }} />
              <Skeleton variant="rectangular" width={120} height={32} animation="wave" sx={{ borderRadius: 2 }} />
            </Box>
            <Skeleton variant="rectangular" height={200} animation="wave" sx={{ borderRadius: 2 }} />
          </Card>
          
          <Card sx={{ p: 3 }}>
            <Skeleton variant="text" width="30%" height={24} animation="wave" sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {Array.from({ length: 3 }).map((_, index) => (
                <Box key={index} sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <Skeleton variant="rectangular" width={60} height={60} animation="wave" sx={{ borderRadius: 1 }} />
                  <Box sx={{ flexGrow: 1 }}>
                    <Skeleton variant="text" width="70%" height={20} animation="wave" />
                    <Skeleton variant="text" width="50%" height={16} animation="wave" />
                  </Box>
                  <Skeleton variant="text" width="20%" height={20} animation="wave" />
                </Box>
              ))}
            </Box>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Card sx={{ p: 3, position: 'sticky', top: 20 }}>
            <Skeleton variant="text" width="40%" height={24} animation="wave" sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3 }}>
              {Array.from({ length: 4 }).map((_, index) => (
                <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Skeleton variant="text" width="60%" height={16} animation="wave" />
                  <Skeleton variant="text" width="30%" height={16} animation="wave" />
                </Box>
              ))}
            </Box>
            <Skeleton variant="rectangular" width="100%" height={48} animation="wave" sx={{ borderRadius: 2 }} />
        </Card>
      </Grid>
    </Grid>
  </Box>
);
};

// Menu Page Skeleton
export const MenuPageSkeleton = () => {
  return (
    <Box sx={{ p: 3 }}>
      <Skeleton variant="text" width="40%" height={40} animation="wave" sx={{ mb: 3 }} />
      
      <Box sx={{ mb: 3 }}>
        <Skeleton variant="text" width="20%" height={24} animation="wave" sx={{ mb: 2 }} />
        <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', pb: 1 }}>
          {Array.from({ length: 6 }).map((_, index) => (
            <Skeleton key={index} variant="rectangular" width={120} height={100} animation="wave" sx={{ borderRadius: 2, flexShrink: 0 }} />
          ))}
        </Box>
      </Box>
      
      <Grid container spacing={2}>
        {Array.from({ length: 8 }).map((_, index) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
            <Card sx={{ height: '100%' }}>
              <Skeleton variant="rectangular" height={200} animation="wave" />
              <CardContent sx={{ p: 2 }}>
                <Skeleton variant="text" width="80%" height={20} animation="wave" sx={{ mb: 1 }} />
                <Skeleton variant="text" width="60%" height={16} animation="wave" sx={{ mb: 1 }} />
                <Skeleton variant="text" width="100%" height={14} animation="wave" sx={{ mb: 2 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Skeleton variant="text" width="30%" height={18} animation="wave" />
                  <Skeleton variant="rectangular" width={80} height={32} animation="wave" sx={{ borderRadius: 2 }} />
      </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
  </Box>
);
};

export default {
  MenuItemSkeleton,
  CategorySkeleton,
  HeroSkeleton,
  OutletCardSkeleton,
  SearchBarSkeleton,
  FilterSectionSkeleton,
  PageHeaderSkeleton,
  CartItemSkeleton,
  LoadingOverlay,
  DashboardSkeleton,
  DeliveryManagementSkeleton,
  PageSkeleton,
  SkeletonTable,
  CheckoutPageSkeleton,
  MenuPageSkeleton,
};
